export type UnitKind = 'list' | 'scalar';

export type ReviewUnit = {
  kind: UnitKind;
  key: string;
  label: string;
  item_count?: number;
};

export function itemTitle(listKey: string, item: unknown, index: number): string {
  const it = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
  if (listKey === 'events') return `event ${it.event_id ?? index + 1}`;
  if (listKey === 'dramatic_scenes') return `scene ${it.scene_id ?? index + 1}`;
  if (listKey === 'value_change_causes') return `cause for ${it.scene_id ?? index + 1}`;
  if (listKey === 'characters') return String(it.name || `char ${index + 1}`);
  if (listKey === 'protagonist_centred_beats') return `beat ${it.beat_id ?? index + 1}`;
  if (listKey === 'dramatic_problems') return `problem ${it.problem_id ?? index + 1}`;
  if (listKey === 'beat_codes') return `${it.beat_id ?? index + 1} -> ${it.code ?? ''}`;
  if (listKey === 'structural_beats') return `stage ${it.stage ?? ''}: ${it.stage_name ?? ''}`;
  if (listKey === 'dialogue_beats') return `beat ${it.beat_id ?? index + 1}`;
  if (listKey === 'beat_emotions') return `${it.beat_id ?? index + 1} -> ${it.dominant_emotion ?? ''}`;
  return `item ${index + 1}`;
}

export function itemNeedle(item: unknown, scalarValue?: unknown): string {
  return itemNeedles(item, scalarValue)[0] || '';
}

export function itemNeedles(item: unknown, scalarValue?: unknown): string[] {
  const found: string[] = [];
  const add = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.replace(/\s+/g, ' ').trim();
    if (trimmed.length < 2) return;
    found.push(trimmed.slice(0, 120));
  };
  add(scalarValue);
  if (item && typeof item === 'object') {
    const it = item as Record<string, unknown>;
    for (const key of ['name', 'text', 'summary', 'dialogue_text', 'statement', 'cause_event_text']) {
      add(it[key]);
    }
    if (Array.isArray(it.aliases)) it.aliases.forEach(add);
    if (Array.isArray(it.speakers)) it.speakers.forEach(add);
  }
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const needle of found.sort((a, b) => b.length - a.length)) {
    if (seen.has(needle)) continue;
    seen.add(needle);
    unique.push(needle);
  }
  return unique;
}

export type ScriptSpan = { start: number; end: number };

function isTokenChar(ch: string | undefined): boolean {
  return !!ch && /[A-Za-z0-9]/.test(ch);
}

const ROLE_WORDS = new Set([
  'TRUCK',
  'DRIVER',
  'OFFICER',
  'COP',
  'POLICE',
  'DETECTIVE',
  'WAITER',
  'WAITRESS',
  'NURSE',
  'DOCTOR',
  'MAN',
  'WOMAN',
  'BOY',
  'GIRL',
  'GUY',
  'GAL',
  'KID',
  'KIDS',
  'GUARD',
  'SOLDIER',
  'NARRATOR',
  'ANNOUNCER',
  'VOICE',
  'CROWD',
  'BOTH',
  'ALL',
  'CREW',
  'ATTENDANT',
  'CLERK',
  'BARTENDER',
  'PILOT',
  'CAPTAIN',
  'SERGEANT',
  'LIEUTENANT',
  'COLONEL',
  'GENERAL',
  'AGENT',
  'INSPECTOR',
  'SHERIFF',
  'MOTHER',
  'FATHER',
  'BROTHER',
  'SISTER',
  'WIFE',
  'HUSBAND',
  'SON',
  'DAUGHTER',
  'MOM',
  'DAD',
  'UNCLE',
  'AUNT',
  'GRANDMOTHER',
  'GRANDFATHER',
  'GRANDMA',
  'GRANDPA',
  'MOB',
  'GROUP',
  'RADIO',
  'PHONE',
  'TV',
  'CAR',
  'BUS',
  'TRAIN',
  'BOAT',
  'PLANE',
  'GUN',
  'DOOR',
  'ROOM',
  'HOUSE',
  'STREET',
  'ROAD',
  'MOOR',
  'SHEEP',
  'HORSE',
  'THE',
  'A',
  'AN',
  'AND',
  'OR',
  'OF',
  'TO',
  'IN',
  'ON',
  'AT',
  'BY',
  'FROM',
  'OS',
  'VO',
  'CONT',
  'CONTD',
  'CONTINUED',
  'MORE',
  'BEAT',
  'PAUSE',
  'CUT',
  'INT',
  'EXT',
  'POV',
  'OFFSCREEN',
  'OFF',
  'CAMERA',
  'YOUNG',
  'OLD',
  'LITTLE',
  'BIG',
]);

function tidyName(value: unknown): string {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

function nameKey(value: unknown): string {
  return tidyName(value).toLowerCase();
}

function characterName(item: unknown): string {
  if (!item || typeof item !== 'object') return '';
  return tidyName((item as Record<string, unknown>).name);
}

function characterAliases(item: unknown): string[] {
  if (!item || typeof item !== 'object') return [];
  const aliases = (item as Record<string, unknown>).aliases;
  return Array.isArray(aliases) ? aliases.map(tidyName).filter(Boolean) : [];
}

export function isProperFirstName(name: string): boolean {
  const trimmed = tidyName(name);
  if (!trimmed || /\s/.test(trimmed)) return false;
  if (trimmed.length < 2 || trimmed.length > 40) return false;
  if (!/^[A-Za-z][A-Za-z'-]*$/.test(trimmed)) return false;
  return !ROLE_WORDS.has(trimmed.toUpperCase());
}

function isRelatedFullName(first: string, other: string): boolean {
  const base = tidyName(first);
  const full = tidyName(other);
  if (!base || !full) return false;
  const parts = full.split(' ');
  if (parts.length < 2) return false;
  if (parts[0].toLowerCase() !== base.toLowerCase()) return false;
  const rest = parts.slice(1);
  if (rest.some((token) => ROLE_WORDS.has(token.toUpperCase()))) return false;
  return rest.every((token) => /^[A-Za-z][A-Za-z'-]*$/.test(token));
}

export function countLabelMentions(script: string, needle: string): number {
  return findAllLabelSpans(script, [needle]).length;
}

export function countMergedMentions(script: string, firstName: string, fullNames: string[]): number {
  const firstSpans = findAllLabelSpans(script, [firstName]);
  const fullSpans = findAllLabelSpans(script, fullNames.map(tidyName).filter(Boolean));
  const nested = firstSpans.filter((first) =>
    fullSpans.some((full) => first.start >= full.start && first.end <= full.end),
  );
  return firstSpans.length - nested.length + fullSpans.length;
}

export type MergeCandidate = {
  name: string;
  scriptCount: number;
  inInventory: boolean;
};

export function relatedCharacterCandidates(
  firstName: string,
  characters: unknown[],
  script: string,
  currentIndex: number,
): MergeCandidate[] {
  if (!isProperFirstName(firstName)) return [];
  const current = characters[currentIndex];
  const already = new Set([firstName, ...characterAliases(current)].map(nameKey).filter(Boolean));
  const byKey = new Map<string, MergeCandidate>();

  const add = (name: string, inInventory: boolean, scriptCount = 0) => {
    const trimmed = tidyName(name);
    if (!trimmed || !isRelatedFullName(firstName, trimmed)) return;
    const key = nameKey(trimmed);
    if (already.has(key)) return;
    const prev = byKey.get(key);
    if (prev) {
      prev.inInventory = prev.inInventory || inInventory;
      if (inInventory) prev.name = trimmed;
      prev.scriptCount = Math.max(prev.scriptCount, scriptCount);
      return;
    }
    byKey.set(key, { name: trimmed, scriptCount, inInventory });
  };

  characters.forEach((character, index) => {
    if (index === currentIndex) return;
    add(characterName(character), true, countLabelMentions(script, characterName(character)));
  });

  const spans = findAllLabelSpans(script, [firstName]);
  const scriptCounts = new Map<string, { name: string; count: number }>();
  for (const span of spans) {
    const rest = script.slice(span.end);
    const match = rest.match(/^(?:[ \t]+|[ \t]*\r?\n[ \t]*)([A-Z][A-Za-z'-]{1,40})/);
    if (!match) continue;
    if (ROLE_WORDS.has(match[1].toUpperCase())) continue;
    const fullEnd = span.end + match[0].length;
    if (isTokenChar(script[fullEnd])) continue;
    const actual = tidyName(script.slice(span.start, fullEnd));
    const key = nameKey(actual);
    const prev = scriptCounts.get(key);
    if (prev) prev.count += 1;
    else scriptCounts.set(key, { name: actual, count: 1 });
  }
  for (const { name, count } of scriptCounts.values()) {
    add(name, false, count);
  }

  return [...byKey.values()].sort(
    (a, b) => b.scriptCount - a.scriptCount || a.name.localeCompare(b.name),
  );
}

function matchPhrase(
  script: string,
  tokens: string[],
  from: number,
  caseSensitive: boolean,
): ScriptSpan | null {
  if (tokens.length === 0) return null;
  const haystack = caseSensitive ? script : script.toLowerCase();
  const parts = caseSensitive ? tokens : tokens.map((token) => token.toLowerCase());
  let searchFrom = from;
  while (searchFrom < haystack.length) {
    const idx = haystack.indexOf(parts[0], searchFrom);
    if (idx < 0) return null;
    if (isTokenChar(script[idx - 1])) {
      searchFrom = idx + Math.max(1, parts[0].length);
      continue;
    }
    let pos = idx + parts[0].length;
    let ok = true;
    for (let i = 1; i < parts.length; i += 1) {
      const ws = /^\s+/.exec(script.slice(pos));
      if (!ws) {
        ok = false;
        break;
      }
      pos += ws[0].length;
      if (haystack.slice(pos, pos + parts[i].length) !== parts[i]) {
        ok = false;
        break;
      }
      pos += parts[i].length;
    }
    if (ok && !isTokenChar(script[pos])) {
      return { start: idx, end: pos };
    }
    searchFrom = idx + Math.max(1, parts[0].length);
  }
  return null;
}

export function findAllLabelSpans(script: string, needles: string[]): ScriptSpan[] {
  if (!script || needles.length === 0) return [];
  const spans: ScriptSpan[] = [];
  const seen = new Set<string>();
  for (const raw of needles) {
    const needle = raw.replace(/\s+/g, ' ').trim();
    if (needle.length < 2) continue;
    const tokens = needle.split(' ').filter(Boolean);
    const caseSensitive = tokens.length > 1;
    let from = 0;
    while (from < script.length) {
      const span = matchPhrase(script, tokens, from, caseSensitive);
      if (!span) break;
      const key = `${span.start}:${span.end}`;
      if (!seen.has(key)) {
        seen.add(key);
        spans.push(span);
      }
      from = span.start + 1;
    }
  }
  return spans;
}

export function acceptedLabelSpans(
  script: string,
  units: ReviewUnit[],
  review: Record<string, unknown>,
  unitIndex: number,
  itemIndex: number,
  skippedNeedles?: Set<string>,
): ScriptSpan[] {
  const needles: string[] = [];
  for (let u = 0; u < units.length; u += 1) {
    if (u > unitIndex) break;
    const unit = units[u];
    if (unit.kind === 'scalar') {
      if (u < unitIndex) needles.push(...itemNeedles(null, review[unit.key]));
      continue;
    }
    const items = (review[unit.key] as unknown[]) || [];
    const last = u < unitIndex ? items.length : itemIndex;
    for (let i = 0; i < last; i += 1) {
      needles.push(...itemNeedles(items[i]));
    }
  }
  const filtered = skippedNeedles?.size
    ? needles.filter((n) => !skippedNeedles.has(n.toLowerCase()))
    : needles;
  return findAllLabelSpans(script, filtered);
}

const LIST_TEXT_FIELD: Record<string, string> = {
  events: 'text',
  dramatic_scenes: 'summary',
  value_change_causes: 'cause_event_text',
  characters: 'name',
  protagonist_centred_beats: 'summary',
  dramatic_problems: 'statement',
  beat_codes: 'beat_id',
  structural_beats: 'summary',
  dialogue_beats: 'dialogue_text',
  beat_emotions: 'beat_id',
};

export function blankListItem(listKey: string): Record<string, unknown> {
  switch (listKey) {
    case 'events':
      return { event_id: '', text: '', start_char: 0, end_char: 0 };
    case 'dramatic_scenes':
      return { scene_id: '', value_polarity_before: '', value_polarity_after: '', summary: '' };
    case 'value_change_causes':
      return { scene_id: '', cause_event_text: '' };
    case 'characters':
      return { name: '', aliases: [], interaction_count: 0 };
    case 'protagonist_centred_beats':
      return { beat_id: '', summary: '', event_refs: [] };
    case 'dramatic_problems':
      return { problem_id: '', statement: '', beat_refs: [] };
    case 'beat_codes':
      return { beat_id: '', code: 'YES' };
    case 'structural_beats':
      return {
        stage: 1,
        stage_name: 'Baseline problem-solving strategy',
        beat_id: '',
        summary: '',
        act: 'act_one',
      };
    case 'dialogue_beats':
      return { beat_id: '', dialogue_text: '', speakers: [], is_dialogue_driven: true };
    case 'beat_emotions':
      return { beat_id: '', dominant_emotion: 'sadness' };
    default:
      return {};
  }
}

export function applySpanToItem(
  listKey: string,
  item: Record<string, unknown>,
  script: string,
  span: ScriptSpan,
  overwriteText = false,
): Record<string, unknown> {
  const excerpt = script.slice(span.start, span.end).replace(/\s+/g, ' ').trim();
  const next: Record<string, unknown> = { ...item, start_char: span.start, end_char: span.end };
  const field = LIST_TEXT_FIELD[listKey];
  if (field && excerpt && (overwriteText || !String(next[field] ?? '').trim())) {
    next[field] = excerpt;
  }
  return next;
}

export function resolveSpan(script: string, item: unknown, scalarValue?: unknown): ScriptSpan | null {
  const fromNeedles = findAllLabelSpans(script, itemNeedles(item, scalarValue));
  if (fromNeedles.length) return fromNeedles[0];
  if (item && typeof item === 'object') {
    const it = item as Record<string, unknown>;
    const start = it.start_char;
    const end = it.end_char;
    if (
      typeof start === 'number' &&
      typeof end === 'number' &&
      start >= 0 &&
      end > start &&
      end <= script.length
    ) {
      return { start, end };
    }
  }
  return null;
}

export function resolveSpans(script: string, item: unknown, scalarValue?: unknown): ScriptSpan[] {
  const fromNeedles = findAllLabelSpans(script, itemNeedles(item, scalarValue));
  if (fromNeedles.length) return fromNeedles;
  const one = resolveSpan(script, item, scalarValue);
  return one ? [one] : [];
}

export function neighborCursor(
  units: ReviewUnit[],
  review: Record<string, unknown>,
  unitIndex: number,
  itemIndex: number,
  delta: -1 | 1,
): { unitIndex: number; listKey: string | null; itemIndex: number } | null {
  let u = unitIndex;
  let i = itemIndex + delta;
  while (u >= 0 && u < units.length) {
    const unit = units[u];
    if (unit.kind === 'scalar') {
      if (delta < 0 && i < 0) {
        u -= 1;
        const prev = units[u];
        if (!prev) return null;
        if (prev.kind === 'list') {
          const items = (review[prev.key] as unknown[]) || [];
          i = Math.max(0, items.length - 1);
        } else {
          i = 0;
        }
        continue;
      }
      if (delta > 0 && i > 0) {
        u += 1;
        i = 0;
        continue;
      }
      return { unitIndex: u, listKey: null, itemIndex: 0 };
    }
    const items = (review[unit.key] as unknown[]) || [];
    if (i >= 0 && i < items.length) {
      return { unitIndex: u, listKey: unit.key, itemIndex: i };
    }
    if (delta > 0) {
      u += 1;
      i = 0;
    } else {
      u -= 1;
      const prev = units[u];
      if (!prev) return null;
      if (prev.kind === 'list') {
        const prevItems = (review[prev.key] as unknown[]) || [];
        i = Math.max(0, prevItems.length - 1);
      } else {
        i = 0;
      }
    }
  }
  return null;
}
